/**
 * Environment detection utilities
 *
 * Centralized logic for determining the current environment to ensure
 * consistency across the application and avoid code duplication.
 *
 * Supports dev/staging/prod environments based on VITE_ENV or NODE_ENV.
 */

export type Environment = 'development' | 'staging' | 'production';

/**
 * Gets the current environment from environment variables
 * Priority: VITE_ENV > NODE_ENV > fallback to 'development'
 */
export const getEnvironment = (): Environment => {
  const raw =
    (import.meta.env.VITE_ENV as string | undefined) ||
    (import.meta.env.MODE as string | undefined) ||
    (import.meta.env.NODE_ENV as string | undefined) ||
    'development';

  if (raw === 'dev' || raw === 'development') return 'development';
  if (raw === 'prod' || raw === 'production') return 'production';
  if (raw === 'staging') return 'staging';
  return 'development';
};

/**
 * Current environment
 */
export const environment = getEnvironment();

/**
 * Determines if the application is running in development environment
 */
export const isDevelopment = environment === 'development';

/**
 * Determines if the application is running in staging environment
 */
export const isStaging = environment === 'staging';

/**
 * Determines if the application is running in production environment
 */
export const isProduction = environment === 'production';

/**
 * Determines if the application is running in a non-production environment
 */
export const isNonProduction = isDevelopment || isStaging;
